import { DeviceModel } from '@lmt/shared-types';
import { AuditSettings } from '@lmt/unicorn-shared/app/components/tenant-settings/components/audit/AuditSettings';
import { AutoLogoutSettings } from '@lmt/unicorn-shared/app/components/tenant-settings/components/AutoLogoutSettings';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { CalendarSettings } from './components/CalendarSettings';
import { GdtSettings } from './components/GdtSettings';
import { Hl7Settings } from './components/hl7/HL7Settings';
import { MeasurementImportSettings } from './components/measurement-import/MeasurementImportSettings';
import { PasswordValidationSettings } from './components/PasswordValidationSettings';

import styles from './components/SettingsBlock.module.scss';
import { useClaims } from '../../features/auth/operations';
import { GeneralSettings } from './components/general/GeneralSettings';
import { useEventCallback } from '@mui/material';
import { NavigationActions } from '../../features/navigation/reducer';
import { useUniDispatch } from '../../store/types';
import { getTabId } from '../../features/navigation/operations';
import { useLocation } from 'react-router-dom';
import { UnsavedChangesContext } from './components/types';

export const TenantSettingsPage: FC = React.memo(function TenantSettingsPage() {
  const claims = useClaims();
  const dispatch = useUniDispatch();
  const location = useLocation();
  const tabId = getTabId(location);
  const [unsavedChanges, setUnsavedChanges] = useState<{
    [key: string]: boolean;
  }>({});
  const changeUnsavedChanges = useCallback((key: string, value: boolean) => {
    setUnsavedChanges((prevValues) => {
      return { ...prevValues, [key]: value };
    });
  }, []);

  const pageHasUnsavedChanges = useMemo(() => {
    return Object.values(unsavedChanges).includes(true);
  }, [unsavedChanges]);

  useEffect(() => {
    setTabHasUnsavedChanges(pageHasUnsavedChanges);
  }, [pageHasUnsavedChanges]);

  const setTabHasUnsavedChanges = useEventCallback((hasChanges: boolean) => {
    if (tabId) {
      dispatch(
        NavigationActions.tabHasUnsavedChangesChanged({
          id: tabId,
          hasUnsavedChanges: hasChanges,
        }),
      );
    }
  });

  return (
    <div className={styles.storedScrolling}>
      <div
        className={styles.settingsContainer}
        data-test-id="tenant-settings-container"
      >
        <UnsavedChangesContext.Provider
          value={{
            setUnsavedChanges: changeUnsavedChanges,
          }}
        >
          <>
            {claims.permissions.tenantSettingsChange && <GeneralSettings />}
            {claims.permissions.tenantSettingsChange && <AutoLogoutSettings />}
            {claims.permissions.tenantSettingsChange && <CalendarSettings />}
            {claims.permissions.gdtManagement && <GdtSettings />}
            {claims.permissions.hl7Management && <Hl7Settings />}
            {claims.permissions.tenantSettingsChange && (
              <MeasurementImportSettings deviceModel={DeviceModel.MiniScreen} />
            )}
            {claims.permissions.tenantSettingsChange &&
              claims.permissions.developerMode && (
                <MeasurementImportSettings deviceModel={DeviceModel.PTSLab} />
              )}
            {claims.permissions.passwordValidationSettings && (
              <PasswordValidationSettings />
            )}
            {claims.permissions.tenantSettingsChange && <AuditSettings />}
          </>
        </UnsavedChangesContext.Provider>
      </div>
    </div>
  );
});
